// Crop and adjust step - positions photos in the selected passport frame.
//
// Shows all photos as thumbnails, allows switching between them.
//

import {
  ArrowLeft,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Move,
  Plus,
  Scan,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/ui/button";

import type { PhotoItem } from "../index";
import { HeadGuideOverlay } from "../ui/head-guide-overlay";
import { Requirements } from "../ui/requirements";
import {
  addWhiteBackground,
  createPreviewDataUrl,
  type CropConfig,
  cropToPhotoSize,
  DEFAULT_PREVIEW_CONTAINER_SIZE,
} from "../utils/canvas";
import { PHOTO_SIZES, type PhotoSize } from "../utils/dimensions";
import {
  calculateFacePositioning,
  detectFace,
} from "../utils/face-detection.client";

// Zoom constraints.
//
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.1;

interface CropAdjustStepProps {
  photos: PhotoItem[];
  activePhotoIndex: number;
  photoSize: PhotoSize;
  removeBackground: boolean;
  onSetActivePhoto: (index: number) => void;
  onCropComplete: (
    croppedImage: Blob,
    config: CropConfig,
    photoId?: string,
  ) => void;
  onAddMorePhotos: (files: File[]) => void;
  onRemovePhoto: (photoId: string) => void;
  onContinue: () => void;
  onBack: () => void;
}

interface PreviewDimensions {
  width: number;
  height: number;
}

function createDefaultCropConfig(
  previewSize: number = DEFAULT_PREVIEW_CONTAINER_SIZE,
): CropConfig {
  return { x: 0, y: 0, zoom: 1, previewSize };
}

function scaleCropConfigForPreview(
  cropConfig: CropConfig,
  previewSize: number,
): CropConfig {
  const sourcePreviewSize =
    cropConfig.previewSize ?? DEFAULT_PREVIEW_CONTAINER_SIZE;
  const safeSourcePreviewSize =
    sourcePreviewSize > 0 ? sourcePreviewSize : DEFAULT_PREVIEW_CONTAINER_SIZE;

  if (safeSourcePreviewSize === previewSize) {
    return { ...cropConfig, previewSize };
  }

  const scale = previewSize / safeSourcePreviewSize;

  return {
    x: cropConfig.x * scale,
    y: cropConfig.y * scale,
    zoom: cropConfig.zoom * scale,
    previewSize,
  };
}

// Calculates the preview frame dimensions from its width and photo ratio.
//
function calculatePreviewDimensions(
  previewWidth: number,
  photoSize: PhotoSize,
): PreviewDimensions {
  const photo = PHOTO_SIZES[photoSize];

  return {
    width: previewWidth,
    height: previewWidth * (photo.pixels.height / photo.pixels.width),
  };
}

// Calculates the zoom needed for an image to cover the full preview frame.
//
function calculateImageFitZoom(
  imageWidth: number,
  imageHeight: number,
  preview: PreviewDimensions,
): number {
  return Math.max(preview.width / imageWidth, preview.height / imageHeight);
}

// Select the processed image only when automatic removal is enabled.
//
function getPhotoSource(photo: PhotoItem, removeBackground: boolean): Blob {
  if (removeBackground && photo.backgroundRemovedImage) {
    return photo.backgroundRemovedImage;
  }

  return photo.originalImage;
}

// Crop and adjust step component.
//
export function CropAdjustStep({
  photos,
  activePhotoIndex,
  photoSize,
  removeBackground,
  onSetActivePhoto,
  onCropComplete,
  onAddMorePhotos,
  onRemovePhoto,
  onContinue,
  onBack,
}: CropAdjustStepProps) {
  const activePhoto = photos[activePhotoIndex];
  const photoSizeConfig = PHOTO_SIZES[photoSize];

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());
  const [config, setConfig] = useState<CropConfig>(
    activePhoto?.cropConfig ?? createDefaultCropConfig(),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [showGuides, setShowGuides] = useState(true);
  const [isAutoFitting, setIsAutoFitting] = useState(false);
  const [autoFitError, setAutoFitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [previewWidth, setPreviewWidth] = useState<number | null>(null);
  const guideDimensions = calculatePreviewDimensions(
    previewWidth ?? DEFAULT_PREVIEW_CONTAINER_SIZE,
    photoSize,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewWidthRef = useRef<number | null>(null);

  const getCurrentPreviewDimensions = useCallback((): PreviewDimensions => {
    const measuredFrame = containerRef.current?.getBoundingClientRect();

    if (measuredFrame && measuredFrame.width > 0 && measuredFrame.height > 0) {
      return {
        width: Math.round(measuredFrame.width),
        height: Math.round(measuredFrame.height),
      };
    }

    return calculatePreviewDimensions(
      previewWidthRef.current ?? DEFAULT_PREVIEW_CONTAINER_SIZE,
      photoSize,
    );
  }, [photoSize]);

  useEffect(() => {
    previewWidthRef.current = previewWidth;
  }, [previewWidth]);

  useEffect(() => {
    const element = containerRef.current;

    if (!element) return;

    const updatePreviewSize = () => {
      const measuredSize = element.getBoundingClientRect().width;

      if (measuredSize > 0) {
        setPreviewWidth(Math.round(measuredSize));
      }
    };

    updatePreviewSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updatePreviewSize);

      return () => window.removeEventListener("resize", updatePreviewSize);
    }

    const observer = new ResizeObserver(updatePreviewSize);
    observer.observe(element);

    return () => observer.disconnect();
  }, [activePhoto?.id, imageUrl, photoSize]);

  useEffect(() => {
    if (!previewWidth) return;

    setConfig((prev) => scaleCropConfigForPreview(prev, previewWidth));
  }, [previewWidth]);

  // Generate thumbnails for all photos.
  //
  useEffect(() => {
    const newThumbnails = new Map<string, string>();
    photos.forEach((photo) => {
      const existingThumbnail = thumbnails.get(photo.id);

      if (existingThumbnail) {
        newThumbnails.set(photo.id, existingThumbnail);
      } else {
        const url = URL.createObjectURL(
          getPhotoSource(photo, removeBackground),
        );
        newThumbnails.set(photo.id, url);
      }
    });

    // Clean up old URLs.
    //
    thumbnails.forEach((url, id) => {
      if (!photos.find((p) => p.id === id)) {
        URL.revokeObjectURL(url);
      }
    });

    setThumbnails(newThumbnails);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thumbnails causes infinite loop
  }, [photos, removeBackground]);

  // Load preview image when active photo changes.
  //
  useEffect(() => {
    if (!activePhoto) return;

    const photo = activePhoto;
    async function loadImage() {
      const url = await createPreviewDataUrl(
        getPhotoSource(photo, removeBackground),
      );
      setImageUrl(url);
      setConfig(
        scaleCropConfigForPreview(
          photo.cropConfig,
          getCurrentPreviewDimensions().width,
        ),
      );
      setIsInitialized(false);
      setAutoFitError(null);
    }

    void loadImage();
  }, [activePhoto, getCurrentPreviewDimensions, removeBackground]);

  // Fit image to container on first load.
  //
  useEffect(() => {
    if (!imageUrl || isInitialized || !activePhoto || !previewWidth) return;

    // Only auto-fit if zoom is at default (1).
    //
    if (activePhoto.cropConfig.zoom !== 1) {
      setIsInitialized(true);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const preview = calculatePreviewDimensions(previewWidth, photoSize);
      const fitZoom = calculateImageFitZoom(img.width, img.height, preview);
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
      setConfig((prev) => ({
        ...prev,
        zoom: clampedZoom,
        previewSize: preview.width,
      }));
      setIsInitialized(true);
    };
    img.src = imageUrl;
  }, [imageUrl, isInitialized, activePhoto, photoSize, previewWidth]);

  // Handle zoom changes.
  //
  const handleZoomIn = () => {
    setConfig((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + ZOOM_STEP, MAX_ZOOM),
    }));
  };

  const handleZoomOut = () => {
    setConfig((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - ZOOM_STEP, MIN_ZOOM),
    }));
  };

  // Handle drag for positioning.
  //
  const handleMouseDown = (e: ReactMouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - config.x, y: e.clientY - config.y });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      setConfig((prev) => ({
        ...prev,
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      }));
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch event handlers for mobile.
  //
  const handleTouchStart = (e: ReactTouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    setIsDragging(true);
    setDragStart({ x: touch.clientX - config.x, y: touch.clientY - config.y });
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      if (!touch) return;
      setConfig((prev) => ({
        ...prev,
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      }));
    },
    [isDragging, dragStart],
  );

  // Attach global mouse/touch listeners for dragging.
  //
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  // Reset position and recalculate fit.
  //
  const handleReset = () => {
    const preview = getCurrentPreviewDimensions();

    if (!imageUrl) {
      setConfig(createDefaultCropConfig(preview.width));
      return;
    }

    const img = new Image();
    img.onload = () => {
      const fitZoom = calculateImageFitZoom(img.width, img.height, preview);
      const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
      setConfig({
        x: 0,
        y: 0,
        zoom: clampedZoom,
        previewSize: preview.width,
      });
    };
    img.src = imageUrl;
  };

  // Auto-fit using face detection.
  //
  const handleAutoFit = async () => {
    if (!imageUrl) return;

    setIsAutoFitting(true);
    setAutoFitError(null);

    try {
      const face = await detectFace(imageUrl, (progress) => {
        console.log("Face detection progress:", progress);
      });

      if (!face) {
        setAutoFitError("No face detected. Please adjust manually.");
        return;
      }

      const preview = getCurrentPreviewDimensions();
      const positioning = calculateFacePositioning(face, preview, photoSize);
      setConfig({
        x: positioning.x,
        y: positioning.y,
        zoom: positioning.zoom,
        previewSize: preview.width,
      });
    } catch (error) {
      console.error("Auto-fit failed:", error);
      setAutoFitError("Auto-fit failed. Please adjust manually.");
    } finally {
      setIsAutoFitting(false);
    }
  };

  // Save crop for current photo.
  //
  const handleSaveCrop = async () => {
    if (!activePhoto) return;

    setIsSaving(true);
    try {
      const preview = getCurrentPreviewDimensions();
      const cropConfig = scaleCropConfigForPreview(config, preview.width);
      const withBackground = await addWhiteBackground(
        getPhotoSource(activePhoto, removeBackground),
      );
      const cropped = await cropToPhotoSize(
        withBackground,
        cropConfig,
        photoSize,
        preview.width,
      );
      onCropComplete(cropped, cropConfig, activePhoto.id);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle adding more photos.
  //
  const handleAddMore = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      onAddMorePhotos(files);
    }
    e.target.value = "";
  };

  // Handle switching to a different photo - auto-save current first.
  //
  const handleSwitchPhoto = async (index: number) => {
    if (index === activePhotoIndex) return;

    // Auto-save current photo before switching.
    //
    if (activePhoto) {
      setIsSaving(true);
      try {
        const preview = getCurrentPreviewDimensions();
        const cropConfig = scaleCropConfigForPreview(config, preview.width);
        const withBackground = await addWhiteBackground(
          getPhotoSource(activePhoto, removeBackground),
        );
        const cropped = await cropToPhotoSize(
          withBackground,
          cropConfig,
          photoSize,
          preview.width,
        );
        onCropComplete(cropped, cropConfig, activePhoto.id);
      } finally {
        setIsSaving(false);
      }
    }

    onSetActivePhoto(index);
  };

  // Calculate fit zoom for an image blob.
  //
  const calculateFitZoom = async (
    blob: Blob,
    preview: PreviewDimensions,
  ): Promise<number> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const fitZoom = calculateImageFitZoom(img.width, img.height, preview);
        const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
        URL.revokeObjectURL(img.src);
        resolve(clampedZoom);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve(1); // Fallback to default zoom
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  // Handle continue - save ALL photos with their current adjustments.
  //
  const handleContinue = async () => {
    setIsSaving(true);
    try {
      const preview = getCurrentPreviewDimensions();

      // Save ALL photos to ensure latest adjustments are captured.
      //
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        if (!photo) continue;

        // For active photo, use local config. For others, use stored config.
        //
        let cropConfigToUse: CropConfig;

        if (i === activePhotoIndex) {
          // Active photo uses local config (has latest adjustments).
          //
          cropConfigToUse = scaleCropConfigForPreview(config, preview.width);
        } else if (
          photo.croppedImage === null &&
          photo.cropConfig.zoom === 1 &&
          photo.cropConfig.x === 0 &&
          photo.cropConfig.y === 0
        ) {
          // Photo was never viewed - calculate auto-fit zoom.
          //
          const fitZoom = await calculateFitZoom(
            getPhotoSource(photo, removeBackground),
            preview,
          );
          cropConfigToUse = {
            x: 0,
            y: 0,
            zoom: fitZoom,
            previewSize: preview.width,
          };
        } else {
          // Use stored config.
          //
          cropConfigToUse = photo.cropConfig;
        }

        const withBackground = await addWhiteBackground(
          getPhotoSource(photo, removeBackground),
        );
        const cropped = await cropToPhotoSize(
          withBackground,
          cropConfigToUse,
          photoSize,
          cropConfigToUse.previewSize,
        );
        onCropComplete(cropped, cropConfigToUse, photo.id);

        // Small delay to allow state update.
        //
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    } finally {
      setIsSaving(false);
    }
    onContinue();
  };

  if (photos.length === 0) {
    return (
      <div className="space-y-6 text-center">
        <input
          ref={fileInputRef}
          multiple
          accept="image/*"
          className="hidden"
          type="file"
          onChange={handleFileInputChange}
        />

        <div>
          <h3 className="text-xl font-semibold">No Photos Selected</h3>
          <p className="text-muted-foreground mt-1">
            Add photos to continue creating your passport print.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <Button onClick={handleAddMore}>
            <Plus className="mr-2 h-4 w-4" />
            Add Photos
          </Button>
        </div>
      </div>
    );
  }

  if (!activePhoto || !imageUrl) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold">Adjust Your Photos</h3>
        <p className="text-muted-foreground mt-1">
          Click a photo to edit. Drag to position and zoom to fit the{" "}
          {photoSizeConfig.label} frame.
        </p>
      </div>

      {/* Hidden file input for adding more photos */}
      <input
        ref={fileInputRef}
        multiple
        accept="image/*"
        className="hidden"
        type="file"
        onChange={handleFileInputChange}
      />

      {/* Photo thumbnails */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {photos.map((photo, index) => {
          const isActive = index === activePhotoIndex;
          const isCropped = photo.croppedImage !== null;

          return (
            <div
              key={photo.id}
              role="button"
              tabIndex={isSaving ? -1 : 0}
              className={`group relative h-16 w-16 cursor-pointer overflow-hidden rounded-lg border-2 transition-all ${
                isActive
                  ? "border-primary ring-primary/20 ring-2"
                  : "border-border hover:border-primary/50"
              } ${isSaving ? "pointer-events-none opacity-50" : ""}`}
              onClick={() => {
                if (!isSaving) {
                  void handleSwitchPhoto(index);
                }
              }}
              onKeyDown={(e) => {
                if (!isSaving && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  void handleSwitchPhoto(index);
                }
              }}
            >
              {thumbnails.has(photo.id) && (
                <img
                  alt={`Photo ${index + 1}`}
                  className="h-full w-full object-cover"
                  src={thumbnails.get(photo.id)}
                />
              )}

              {/* Cropped indicator */}
              {isCropped && (
                <div className="absolute right-0 bottom-0 flex h-5 w-5 items-center justify-center rounded-tl bg-green-500 text-white">
                  <Check className="h-3 w-3" />
                </div>
              )}

              {/* Photo number */}
              <div className="absolute top-0 left-0 flex h-5 w-5 items-center justify-center rounded-br bg-black/50 text-xs text-white">
                {index + 1}
              </div>

              {/* Remove button */}
              <button
                className="absolute top-0 right-0 flex h-5 w-5 items-center justify-center rounded-bl bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePhoto(photo.id);
                }}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}

        {/* Add more button */}
        <button
          className="text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-accent flex h-16 w-16 items-center justify-center rounded-lg border-2 border-dashed transition-colors"
          type="button"
          onClick={handleAddMore}
        >
          <Plus className="h-6 w-6" />
        </button>
      </div>

      {/* Crop area */}
      <div className="flex justify-center">
        <div
          ref={containerRef}
          className="border-primary relative w-full cursor-move overflow-hidden rounded-xl border-4 bg-white shadow-lg"
          style={{
            aspectRatio: `${photoSizeConfig.pixels.width} / ${photoSizeConfig.pixels.height}`,
            maxWidth: DEFAULT_PREVIEW_CONTAINER_SIZE,
            backgroundImage: `
              linear-gradient(45deg, #e5e7eb 25%, transparent 25%),
              linear-gradient(-45deg, #e5e7eb 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #e5e7eb 75%),
              linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)
            `,
            backgroundSize: "20px 20px",
            backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <img
            alt="Uploaded photo"
            className="pointer-events-none absolute select-none"
            draggable={false}
            src={imageUrl}
            style={{
              left: "50%",
              top: "50%",
              transform: `translate(calc(-50% + ${config.x}px), calc(-50% + ${config.y}px)) scale(${config.zoom})`,
              maxWidth: "none",
            }}
          />

          {showGuides && (
            <HeadGuideOverlay
              containerHeight={guideDimensions.height}
              containerWidth={guideDimensions.width}
              photoSize={photoSize}
            />
          )}
        </div>
      </div>

      {showGuides && (
        <p className="text-muted-foreground text-center text-xs">
          Align the top of the head with the blue line and place the bottom of
          the chin inside the yellow band.
        </p>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button size="sm" variant="outline" onClick={handleZoomOut}>
          <ZoomOut className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-1">
          <input
            className="w-16 rounded border px-2 py-1 text-center text-sm"
            max={Math.round(MAX_ZOOM * 100)}
            min={Math.round(MIN_ZOOM * 100)}
            type="number"
            value={Math.round(config.zoom * 100)}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value)) {
                const zoom = Math.max(
                  MIN_ZOOM,
                  Math.min(MAX_ZOOM, value / 100),
                );
                setConfig((prev) => ({ ...prev, zoom }));
              }
            }}
          />
          <span className="text-muted-foreground text-sm">%</span>
        </div>
        <Button size="sm" variant="outline" onClick={handleZoomIn}>
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={handleReset}>
          <Move className="mr-1 h-4 w-4" />
          Reset
        </Button>
      </div>

      {/* Auto-fit and guide controls */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          disabled={isAutoFitting || isSaving}
          size="sm"
          variant="default"
          onClick={() => {
            void handleAutoFit();
          }}
        >
          <Scan className="mr-1 h-4 w-4" />
          {isAutoFitting ? "Detecting face..." : "Auto-fit Face"}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowGuides(!showGuides)}
        >
          {showGuides ? (
            <>
              <EyeOff className="mr-1 h-4 w-4" />
              Hide Guides
            </>
          ) : (
            <>
              <Eye className="mr-1 h-4 w-4" />
              Show Guides
            </>
          )}
        </Button>
      </div>

      {/* Status messages */}
      {isAutoFitting && (
        <div className="text-muted-foreground bg-accent flex items-center justify-center gap-3 rounded-xl p-3 text-sm">
          <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
          <span>
            Detecting face (first time may take a moment to load model)...
          </span>
        </div>
      )}
      {autoFitError && !isAutoFitting && (
        <div className="rounded-xl bg-amber-50 p-3 text-center text-sm text-amber-700 dark:bg-amber-950 dark:text-amber-300">
          {autoFitError}
        </div>
      )}

      <div className="flex justify-center">
        <Button
          disabled={isSaving}
          size="sm"
          variant="outline"
          onClick={() => {
            void handleSaveCrop();
          }}
        >
          <Check className="mr-1 h-4 w-4" />
          {isSaving ? "Saving..." : "Save Crop"}
        </Button>
      </div>

      {/* Crop status */}
      <div className="text-muted-foreground text-center text-sm">
        {photos.filter((p) => p.croppedImage !== null).length} of{" "}
        {photos.length} photos cropped
      </div>

      {/* Requirements */}
      <Requirements photoSize={photoSize} />

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          disabled={isSaving}
          onClick={() => {
            void handleContinue();
          }}
        >
          {isSaving ? "Saving..." : "Continue"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
